// FP Directory - Franchise Partner listing page with card-based layout

import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'

import { apiClient } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import type { FranchisePartner } from './types'
import { DirectoryHeader, DirectoryFilterCard, FPCard, FPMobileCard } from './components'
import { useDirectoryFilters } from './hooks/useDirectoryFilters'
import { usePWAInstall } from './hooks/usePWAInstall'

const FPDirectory: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()
  const { loading: authLoading, isAuthenticated } = useAuth()
  const { isInstallable, promptInstall } = usePWAInstall()

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated && !authLoading) {
      const redirectPath = location.pathname + location.search
      window.location.href = `/login?redirect=${encodeURIComponent(redirectPath)}`
    }
  }, [isAuthenticated, authLoading, location])

  const { data, isLoading, error } = useQuery({
    queryKey: ['fp-directory'],
    queryFn: () => apiClient.get('/franchise-partners'),
    enabled: isAuthenticated,
  })

  // Use the directory filters hook
  const partners = data?.data?.partners || []
  const {
    filters,
    updateFilter,
    clearFilters,
    filteredData: filteredPartners,
    hasActiveFilters
  } = useDirectoryFilters(partners)

  // Show loading while checking auth
  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null
  }

  return (
    <>
      <Helmet>
        <title>Franchise Partner Directory - Poolwerx</title>
      </Helmet>

      <Box sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        pb: 3
      }}>
        <DirectoryHeader
          partnerCount={filteredPartners.length}
          isMobile={isMobile}
          isInstallable={isInstallable}
          onInstall={promptInstall}
        />

        {/* Content */}
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
          {/* Filter Bar */}
          <DirectoryFilterCard
            filters={filters}
            onFilterChange={updateFilter}
            onClearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
            loading={isLoading}
          />

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load franchise partners. Please try refreshing the page.
            </Alert>
          )}

          {!isLoading && !error && filteredPartners.length === 0 && (
            <Alert severity="info">
              {hasActiveFilters ? 'No franchise partners match your filters.' : 'No franchise partners found.'}
            </Alert>
          )}

          {!isLoading && !error && filteredPartners.length > 0 && (
            isMobile ? (
              // Mobile: Compact card view
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {filteredPartners.length} partners
                </Typography>
                {filteredPartners.map((fp: FranchisePartner) => (
                  <FPMobileCard key={fp.fp_code} fp={fp} />
                ))}
              </Box>
            ) : (
              // Desktop: 2-3 cards per row grid
              <Grid container spacing={2}>
                {filteredPartners.map((fp: FranchisePartner) => (
                  <Grid item xs={12} lg={6} xl={4} key={fp.fp_code}>
                    <FPCard fp={fp} />
                  </Grid>
                ))}
              </Grid>
            )
          )}
        </Box>
      </Box>
    </>
  )
}

export default FPDirectory
